import React, { useState } from 'react';
import {
Box,
Paper,
Typography,
TextField,
Button,
Alert,
CircularProgress,
Chip,
Tooltip,
Dialog,
DialogTitle,
DialogContent,
DialogActions
} from '@mui/material';
import {
Send as SendIcon,
InfoOutlined as InfoOutlinedIcon,
Warning as WarningIcon,
Edit as EditIcon
} from '@mui/icons-material';
import { addDoc, collection, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const QuestionForm = ({
  clientId,
  user,
  clientIsActive,
  clients,
  onQuestionSubmitted
}) => {
  // Form state
  const [questionText, setQuestionText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Governance flag state
  const [governanceFlag, setGovernanceFlag] = useState(null); // holds governance details when flagged
  const [editedQuestion, setEditedQuestion] = useState('');
  const [resubmitting, setResubmitting] = useState(false);

  // Get current client info
  const getCurrentClient = () => {
    if (clientId === 'ALL' || !clientId) return null;
    return clients?.find(c => c.id === clientId);
  };

  const currentClient = getCurrentClient();

  // Determine if the text box should be disabled
  const isTextBoxDisabled = clientId === 'ALL' || !clientIsActive;
  const canSubmit = clientId && clientId !== 'ALL' && clientIsActive && questionText.trim();

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canSubmit || submitting) return;

    const userId = user?.uid || user?.id;
    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading("Submitting your question...");

    let newQuestionId = null;

    try {
      // Step 1: Create the initial question document in Firestore to get an ID
      const currentClient = clients?.find(c => c.id === clientId);
      const questionData = {
        questionText: questionText.trim(),
        clientId: clientId,
        organizationId: currentClient?.organizationId || null,
        userId: userId,
        fcfoId: userId, // For backwards compatibility
        userEmail: user.email,
        status: 'pending',
        aiStatus: 'processing', // Indicates the AI pipeline is running
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Initialize other fields from our data model
        type: 'standard',
        category: 'general',
        answer: null,
        feedback: null,
        favoritedBy: [],
        userNotes: ""
      };

      const docRef = await addDoc(collection(db, 'questions'), questionData);
      newQuestionId = docRef.id;
      
      toast.update(toastId, { render: "Question saved. Triggering AI analysis...", type: "info" });

      // Step 2: Trigger the 'pocAutomatedAnswer' Cloud Function with the new ID
      const pocAutomatedAnswer = httpsCallable(functions, 'pocAutomatedAnswer', { timeout: 300000 }); // 300,000ms = 5 minutes
      await pocAutomatedAnswer({
        userQuestion: questionText.trim(),
        clientId: clientId,
        questionId: newQuestionId,
      });

      toast.update(toastId, { render: "AI processing started! Your answer will appear shortly.", type: "success", isLoading: false, autoClose: 5000 });
      
      setQuestionText('');
      if (onQuestionSubmitted) {
        onQuestionSubmitted();
      }

    } catch (error) {
        console.error("Error during question submission pipeline:", error);

        // Check if this is a governance flag (edit & resubmit flow)
        const details = error?.details;
        if (details?.governanceFlag) {
            toast.update(toastId, { render: "Your question was flagged for privacy review.", type: "warning", isLoading: false, autoClose: 5000 });
            // Update question doc to flagged status
            if (newQuestionId) {
                const questionRef = doc(db, 'questions', newQuestionId);
                await updateDoc(questionRef, { status: 'governance_flagged', aiStatus: 'governance_flagged' });
            }
            // Open the edit & resubmit modal
            setGovernanceFlag({
                ...details,
                questionId: newQuestionId,
                originalText: questionText.trim(),
            });
            setEditedQuestion(questionText.trim());
        } else {
            toast.update(toastId, { render: `An error occurred: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
            if (newQuestionId) {
                const questionRef = doc(db, 'questions', newQuestionId);
                await updateDoc(questionRef, { status: 'error', aiStatus: 'error' });
            }
        }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit & resubmit from governance flag modal
  const handleResubmit = async () => {
    if (!editedQuestion.trim() || !governanceFlag?.questionId) return;

    setResubmitting(true);
    const toastId = toast.loading("Resubmitting your edited question...");

    try {
      // Update the existing question document with edited text
      const questionRef = doc(db, 'questions', governanceFlag.questionId);
      await updateDoc(questionRef, {
        questionText: editedQuestion.trim(),
        status: 'pending',
        aiStatus: 'processing',
        updatedAt: serverTimestamp(),
        governanceResubmitted: true,
        previousText: governanceFlag.originalText,
      });

      // Re-trigger AI processing
      const pocAutomatedAnswer = httpsCallable(functions, 'pocAutomatedAnswer', { timeout: 300000 });
      await pocAutomatedAnswer({
        userQuestion: editedQuestion.trim(),
        clientId: clientId,
        questionId: governanceFlag.questionId,
      });

      toast.update(toastId, { render: "Question resubmitted successfully! Answer will appear shortly.", type: "success", isLoading: false, autoClose: 5000 });
      setGovernanceFlag(null);
      setEditedQuestion('');
      setQuestionText('');
      if (onQuestionSubmitted) onQuestionSubmitted();

    } catch (error) {
      console.error("Error resubmitting question:", error);
      // Check if the resubmission was also flagged
      if (error?.details?.governanceFlag) {
        toast.update(toastId, { render: "Still flagged. Please remove more sensitive information.", type: "warning", isLoading: false, autoClose: 5000 });
        setGovernanceFlag(prev => ({
          ...prev,
          ...error.details,
          originalText: prev.originalText,
        }));
        setEditedQuestion(editedQuestion.trim());
      } else {
        toast.update(toastId, { render: `Error: ${error.message}`, type: "error", isLoading: false, autoClose: 5000 });
      }
    } finally {
      setResubmitting(false);
    }
  };

  // Dismiss governance flag (user chooses not to resubmit)
  const handleDismissFlag = async () => {
    // Leave the question doc as governance_flagged — it shows in their history
    setGovernanceFlag(null);
    setEditedQuestion('');
  };

  return (
    <Paper elevation={2} sx={{ p: 3, height: 'fit-content' }}>
      {/* Main Flex Header */}
<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.3rem' }}>
            Ask a Question
        </Typography>
        <Tooltip title="Questions are AI-processed and typically answered within minutes.  For best privacy, avoid entering client or personal names in your questions.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
    </Box>
    {clientId && clientId !== 'ALL' && currentClient && (
        <Chip
            label={`To: ${currentClient.displayName || currentClient.name}`}
            color="primary"
            variant="outlined"
        />
    )}
</Box>

{/* Conditional Alerts Area */}
{clientId === 'ALL' && (
    <Alert severity="info" sx={{ mb: 2 }}>
        Select a specific client to ask a question.
    </Alert>
)}
{!clientIsActive && clientId && clientId !== 'ALL' && (
    <Alert severity="warning" sx={{ mb: 2 }}>
        This client is inactive. Questions cannot be submitted.
    </Alert>
)}

      <form onSubmit={handleSubmit}>
        {/* Question Text Input */}
        <TextField
          multiline
          rows={4}
          fullWidth
          label="Your Question"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="What would you like to know about your business?"
          disabled={isTextBoxDisabled}
          sx={{
            mb: 2,
            '& .MuiInputBase-input': {
              fontSize: '1.1rem'
            },
            '& .MuiInputLabel-root': {
              fontSize: '1.1rem'
            }
          }}
          inputProps={{ maxLength: 2000 }}
          helperText={`${questionText.length}/2000 characters`}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          disabled={!canSubmit || submitting}
          sx={{
            py: 1.5,
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Question'}
        </Button>

        {/* Help text removed to save vertical space */}

      </form>

      {/* Governance Flag — Edit & Resubmit Modal */}
      <Dialog open={!!governanceFlag} onClose={handleDismissFlag} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.dark' }}>
          <WarningIcon color="warning" />
          Question Flagged for Privacy Review
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your question was flagged because it may contain sensitive information.
            {governanceFlag?.totalPiiFound > 0 && ` (${governanceFlag.totalPiiFound} item${governanceFlag.totalPiiFound !== 1 ? 's' : ''} detected)`}
          </Alert>

          {/* DLP Findings */}
          {governanceFlag?.dlpFindings?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Detected:
              </Typography>
              <Box display="flex" gap={0.5} sx={{ mt: 0.5 }}>
                {governanceFlag.dlpFindings.map((finding, idx) => (
                  <Chip
                    key={idx}
                    label={`${finding.infoType.replace(/_/g, ' ')}: ${finding.count}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Sanitized preview */}
          {governanceFlag?.sanitizedPreview && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                After sanitization (items replaced with [person], [email], etc.):
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {governanceFlag.sanitizedPreview}
              </Typography>
            </Paper>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Edit your question below to remove sensitive information, then resubmit. 
            Avoid including personal names, email addresses, or other identifying details.
          </Typography>

          {/* Editable question */}
          <TextField
            multiline
            rows={4}
            fullWidth
            label="Edit Your Question"
            value={editedQuestion}
            onChange={(e) => setEditedQuestion(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${editedQuestion.length}/2000 characters`}
            sx={{
              '& .MuiInputBase-input': { fontSize: '1.1rem' },
              '& .MuiInputLabel-root': { fontSize: '1.1rem' }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissFlag} disabled={resubmitting}>
            Dismiss
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={resubmitting ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            disabled={resubmitting || !editedQuestion.trim()}
            onClick={handleResubmit}
          >
            {resubmitting ? 'Resubmitting...' : 'Edit & Resubmit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default QuestionForm;